import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

// green: on/ahead of goal and nothing overdue
// amber: modestly behind goal, or a minor overdue invoice, or no goal data to judge by but an unpaid invoice exists
// red: well behind goal, or an overdue invoice, or clearly declining
function computeHealth(attainmentPct, hasOverdueInvoice) {
  if (hasOverdueInvoice) return attainmentPct !== null && attainmentPct < 60 ? "red" : "amber";
  if (attainmentPct === null) return "green";
  if (attainmentPct >= 90) return "green";
  if (attainmentPct >= 60) return "amber";
  return "red";
}

export default function ClientPortfolio() {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clientFinancials, setClientFinancials] = useState([]);
  const [metricsThisMonth, setMetricsThisMonth] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const thisMonth = new Date().toISOString().slice(0, 7);

    const [clientsRes, projectsRes, invoicesRes, cfRes, metricsRes, dealsRes] = await Promise.all([
      supabase.from("clients").select("*"),
      supabase.from("projects").select("id, client_id, goal_targets"),
      supabase.from("invoices").select("*"),
      supabase.from("client_financials").select("*").eq("month", `${thisMonth}-01`),
      supabase.from("metrics").select("*, projects!inner(client_id)").gte("date", `${thisMonth}-01`),
      supabase.from("deals").select("*"),
    ]);

    setClients(clientsRes.data || []);
    setProjects(projectsRes.data || []);
    setInvoices(invoicesRes.data || []);
    setClientFinancials(cfRes.data || []);
    setMetricsThisMonth(metricsRes.data || []);
    setDeals(dealsRes.data || []);
    setLoading(false);
  }

  const rows = useMemo(() => {
    return clients.map((c) => {
      const clientInvoices = invoices.filter((i) => i.client_id === c.id);
      const revenue = clientInvoices
        .filter((i) => i.invoice_date?.slice(0, 7) === new Date().toISOString().slice(0, 7))
        .reduce((sum, i) => sum + Number(i.amount), 0);
      const hasOverdue = clientInvoices.some((i) => i.status === "overdue");

      const cf = clientFinancials.find((f) => f.client_id === c.id);
      const margin = cf && revenue > 0 ? Math.round(((revenue - Number(cf.cost)) / revenue) * 100) : null;

      const clientProjects = projects.filter((p) => p.client_id === c.id);
      let attainment = null;
      const goalPcts = [];
      clientProjects.forEach((p) => {
        const targets = p.goal_targets || {};
        Object.entries(targets).forEach(([key, target]) => {
          const actual = metricsThisMonth
            .filter((m) => m.project_id === p.id && m.metric_key === key)
            .reduce((sum, m) => sum + Number(m.value), 0);
          if (Number(target) > 0) goalPcts.push((actual / Number(target)) * 100);
        });
      });
      if (goalPcts.length > 0) {
        attainment = Math.round(goalPcts.reduce((a, b) => a + b, 0) / goalPcts.length);
      }

      const renewalDays = daysUntil(c.contract_renewal_date);
      const health = computeHealth(attainment, hasOverdue);

      return { ...c, revenue, margin, attainment, renewalDays, health };
    });
  }, [clients, invoices, clientFinancials, projects, metricsThisMonth]);

  const kpis = useMemo(() => {
    const total = clients.length;
    const thisMonth = new Date().toISOString().slice(0, 7);
    const newThisMonth = clients.filter((c) => c.created_at?.slice(0, 7) === thisMonth).length;
    const retained = clients.filter((c) => c.lifecycle_status === "active").length;
    const churned = clients.filter((c) => c.lifecycle_status === "churned").length;
    const retention = retained + churned > 0 ? Math.round((retained / (retained + churned)) * 100) : 100;
    const churnRate = total > 0 ? Math.round((churned / total) * 100) : 0;
    const renewalsDue = clients.filter((c) => {
      const d = daysUntil(c.contract_renewal_date);
      return d !== null && d >= 0 && d <= 60;
    }).length;
    const openUpsell = deals.filter((d) => d.deal_type === "upsell" && !["won", "lost"].includes(d.stage)).length;
    const openCrossSell = deals.filter((d) => d.deal_type === "cross_sell" && !["won", "lost"].includes(d.stage)).length;

    return { total, newThisMonth, retained, churned, retention, churnRate, renewalsDue, openUpsell, openCrossSell };
  }, [clients, deals]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Client portfolio</h1>
      <p className="text-sm text-muted-foreground mb-5">Retention, churn, and per-client health</p>

      <div className="grid grid-cols-5 gap-3 mb-5">
        <Kpi label="Total clients" value={kpis.total} />
        <Kpi label="New this month" value={kpis.newThisMonth} />
        <Kpi label="Retained" value={kpis.retained} />
        <Kpi label="Churned" value={kpis.churned} />
        <Kpi label="Retention" value={`${kpis.retention}%`} />
        <Kpi label="Churn" value={`${kpis.churnRate}%`} />
        <Kpi label="Renewals due, 60 days" value={kpis.renewalsDue} />
        <Kpi label="Upsell opportunities" value={kpis.openUpsell} />
        <Kpi label="Cross-sell opportunities" value={kpis.openCrossSell} />
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">Client health</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <Th>Client</Th>
              <Th align="right">Revenue</Th>
              <Th align="right">Margin</Th>
              <Th align="right">Performance</Th>
              <Th>Renewal</Th>
              <Th>Health</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0">
                <td className="px-5 py-3.5 font-medium text-foreground">{r.company_name}</td>
                <td className="px-5 py-3.5 text-right">₹{r.revenue.toLocaleString("en-IN")}</td>
                <td className="px-5 py-3.5 text-right">{r.margin !== null ? `${r.margin}%` : "-"}</td>
                <td className="px-5 py-3.5 text-right">{r.attainment !== null ? `${r.attainment}%` : "-"}</td>
                <td className="px-5 py-3.5 text-muted-foreground">
                  {r.renewalDays !== null ? `${r.renewalDays} days` : "-"}
                </td>
                <td className="px-5 py-3.5">
                  <HealthDot health={r.health} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-border rounded-2xl bg-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</div>
      <div className="text-lg font-semibold text-foreground">{value}</div>
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th className={`text-${align || "left"} font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3`}>
      {children}
    </th>
  );
}

function HealthDot({ health }) {
  const colors = { green: "bg-primary", amber: "bg-yellow-600", red: "bg-destructive" };
  const labels = { green: "Good", amber: "Watch", red: "At risk" };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={`w-2.5 h-2.5 rounded-full ${colors[health]}`} />
      {labels[health]}
    </span>
  );
}
